<template>
  <div>
    <v-modal short @close="$emit('close')">
      <template slot="#thread-participants">
        <div
          v-if="isLoading"
          class="d-flex justify-content-center text-muted mt-5 pt-5"
        >
          <v-loader />
        </div>
        <div v-else class="participants__container d-flex flex-column">
          <action-header>
            {{
              $t("components.modals.threadParticipantsModal.participantTitle")
            }}
          </action-header>
          <action-body>
            <div
              v-if="selfParticipant"
              secondary
              ghost
              class="d-flex align-items-center px-2 py-1 w-100"
            >
              <subscriber-avatar
                class="mr-2"
                :subscriber="selfParticipant.user"
                small
              />
              <div class="d-flex flex-column justify-content-center name">
                {{ $t("components.modals.threadParticipantsModal.youTitle") }}
              </div>
            </div>
            <div
              v-for="participant in shownParticipants"
              :key="participant.user.id"
              class="d-flex align-items-center px-2 py-1"
            >
              <subscriber-avatar
                class="mr-2"
                :subscriber="participant.user"
                small
              />

              <div class="name">
                {{ participant.user.name }}
              </div>
              <div
                v-if="isAdmin"
                class="ml-auto remove-button"
                @click="() => (removingParticipant = participant)"
              >
                <v-icon icon="cancel" size="2rem"></v-icon>
              </div>
            </div>
          </action-body>
        </div>
      </template>
    </v-modal>

    <alert-dialog
      v-if="removingParticipant"
      :title="
        $t('components.modals.threadParticipantsModal.dialogRemoveConfirm')
      "
      :actions="[
        {
          title: $t(
            'components.modals.threadParticipantsModal.dialogRemoveCancel'
          ),
          callback: () => (removingParticipant = null)
        },
        {
          title: $t(
            'components.modals.threadParticipantsModal.dialogRemoveYes'
          ),
          callback: handleRemoveParticipant
        }
      ]"
      :show="!!removingParticipant"
      @close="() => (removingParticipant = null)"
    >
      {{
        $t("components.modals.threadParticipantsModal.confirmTitle", {
          user_name: removingParticipant.user.name
        })
      }}
    </alert-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex"

import SubscriberAvatar from "@/components/SubscriberAvatar"
import ActionHeader from "@/components/modals/shared/ActionHeader"
import ActionBody from "@/components/modals/shared/ActionBody"

export default {
  components: {
    SubscriberAvatar,
    ActionHeader,
    ActionBody,
    AlertDialog: () => import("@/components/AlertDialog")
  },

  props: {
    messageThreadId: { type: [Number, String], required: true }
  },

  data() {
    return {
      isLoading: false,
      participants: [],
      removingParticipant: false
    }
  },

  computed: {
    ...mapGetters("auth", ["isAuthenticated", "currentUser"]),

    isAdmin() {
      return this.isAuthenticated && this.currentUser?.isAccountAdmin
    },

    selfParticipant() {
      return (
        this.participants.find(
          participant => participant.user.id === this.currentUser?.id
        ) || null
      )
    },

    shownParticipants() {
      return this.participants.filter(
        participant => participant.user.id !== this.currentUser?.id
      )
    }
  },

  watch: {
    isAuthenticated: {
      immediate: true,
      handler: function() {
        if (this.isAuthenticated) {
          this.isLoading = true

          this.fetchParticipants(this.messageThreadId)
            .then(participants => {
              this.participants = participants
            })
            .finally(() => {
              this.isLoading = false
            })
        }
      }
    }
  },

  methods: {
    ...mapActions("messages", [
      "fetchParticipants",
      "removeParticipantFromThread"
    ]),

    handleRemoveParticipant() {
      const removingUserId = this.removingParticipant.user.id
      if (!removingUserId) return

      this.removeParticipantFromThread({
        messageThreadId: this.messageThreadId,
        userId: removingUserId
      })
        .then(() => {
          this.participants = this.participants.filter(
            participant => participant.user.id != removingUserId
          )

          this.$notify("Successfully Removed")
        })
        .catch(() => {
          this.$notify("An Error Occurred")
        })
        .finally(() => {
          this.removingParticipant = null
        })
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";
@import "@/assets/styles/mixins";

.participants__container {
  .name {
    font-weight: 600;
  }

  .remove-button {
    @include clickable;

    .v-icon {
      color: $color-ui--grey-60;
    }
  }
}
</style>
